import { Scheme_fromCardSchemeGql } from "@npco/zeller-design-system";
import { useCallback } from "react";
import { twMerge } from "tailwind-merge";

import { formatCurrency, formatDateTimestamp } from "../../../../../@lib-i18n";
import { getMethod } from "../shared/transaction-utils";
import type { TransactionsList } from "../transactions-list";

export const TransactionsListItemInput = ({
	disabled: disabledProp,
	name,
	onChange,
	readOnly,
	value,
	transaction,
}: { transaction: TransactionsList.Option } & Pick<
	TransactionsList.Props,
	"name" | "onChange" | "value" | "disabled" | "readOnly"
>) => {
	const { id } = transaction;
	const onSelectOption = useCallback(() => {
		onChange?.(id);
	}, [onChange, id]);

	const balanceAmount = transaction.amount - (transaction.refundedAmount ?? 0);
	const isFullyRefunded = balanceAmount === 0;
	const refundType = (() => {
		if (isFullyRefunded) return "full";
		if (balanceAmount < transaction.amount) return "partial";
		return undefined;
	})();

	const disabled = disabledProp || isFullyRefunded;

	return (
		<li
			className="list-none"
			role="option"
			aria-label={`${refundType ? "refunded " : ""}sale of ${formatCurrency({
				amountInCents: transaction.amount,
				currency: transaction.currency ?? undefined,
			})} of reference #${transaction.reference} ${
				transaction.externalReference &&
				transaction.externalReference !== transaction.reference
					? `(external reference ${transaction.externalReference}) `
					: ""
			}at ${formatDateTimestamp(
				transaction.timestamp,
			)} with card ending in ${transaction.maskedPan?.replace("…", "")}`}
		>
			<input
				checked={id === value}
				className="peer hidden"
				disabled={disabled}
				id={id}
				name={name}
				onChange={onSelectOption}
				readOnly={readOnly}
				type="radio"
				value={id}
			/>
			<TransactionsListItemDisplay
				disabled={disabled}
				readOnly={readOnly}
				refundType={refundType}
				{...transaction}
			/>
		</li>
	);
};

export const TransactionsListItemDisplay = ({
	amount,
	currency,
	disabled,
	externalReference,
	id,
	maskedPan,
	readOnly,
	reference,
	refundedAmount,
	scheme: transactionScheme,
	timestamp,
	refundType,
}: TransactionsList.Option & {
	readOnly?: boolean;
	disabled?: boolean;
	refundType?: "full" | "partial";
}) => {
	const scheme = Scheme_fromCardSchemeGql(transactionScheme);
	const refundText =
		refundType === "full"
			? "Refunded"
			: `${formatCurrency({
					amountInCents: refundedAmount ?? 0,
					currency: currency ?? undefined,
				})} Partially Refunded`;

	return (
		<label
			htmlFor={id}
			aria-disabled={disabled}
			className={twMerge(
				"h-[64px] w-full",
				"flex items-center",
				"rounded-8",
				"pl-4 pr-16",

				"bg-white",
				"dark:bg-grey-900",

				"border",
				"border-grey-150",
				"dark:border-grey-800",

				"peer-checked:border-blue-1000",
				"peer-checked:bg-blue-80",
				"peer-checked:dark:bg-blue-5000",

				"aria-disabled:cursor-not-allowed",
				"aria-disabled:text-grey-250",
				"aria-disabled:bg-grey-30",
				"aria-disabled:dark:text-grey-470",
				"aria-disabled:dark:bg-grey-900",

				"cursor-pointer",
				readOnly && "cursor-auto",
				disabled && "pointer-events-none",
			)}
		>
			<div
				aria-label={scheme.label}
				aria-disabled={disabled}
				className="ml-8 mr-12 h-[40px] w-[40px] rounded-4 bg-white aria-disabled:opacity-20"
			>
				{scheme.icon}
			</div>

			<div className="flex w-full justify-between gap-1">
				<div className="flex w-full flex-col">
					<div className="text-base">{getMethod({ maskedPan })}</div>
					<div
						aria-disabled={disabled}
						className="text-xs text-grey-550 dark:text-grey-400 aria-disabled:text-grey-250"
					>
						{formatDateTimestamp(timestamp)}
					</div>
				</div>

				<div className="flex w-full flex-col">
					<div className="text-base">#{reference}</div>
					{externalReference && (
						<div
							aria-disabled={disabled}
							className="text-xs text-grey-550 dark:text-grey-400 aria-disabled:text-grey-250"
						>
							External: {externalReference}
						</div>
					)}
				</div>
			</div>

			<div className="flex min-w-[122px] flex-col text-right flex-shrink-0">
				<div className="text-base">
					{formatCurrency({
						amountInCents: amount,
						currency: currency ?? undefined,
					})}
				</div>
				{refundType && (
					<div
						aria-disabled={disabled}
						className="text-xs text-grey-550 dark:text-grey-400 aria-disabled:text-grey-250"
					>
						{refundText}
					</div>
				)}
			</div>
		</label>
	);
};
